import PropTypes from 'prop-types';

import {Box, Flex, Heading, Skeleton, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {HeroBannerBackgroundMedia} from '@amblique/king-living/app/components/hero-banner-background-media';
import {
  hasHeroBannerBackgroundMedia,
  hasHeroBannerVideo,
  HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS,
} from '@amblique/king-living/app/utils/hero-banner-video';

/**
 * Hero Banner Block (CF-06)
 * Background image/video on top with content section below (title left, subtitle right)
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"wrapper" | "mediaWrapper" | "backgroundMedia" | "contentSection" | "titleColumn" | "title" | "subtitleColumn" | "subtitle"} HeroBannerBlockCF06Parts
 * @typedef {Partial<Record<HeroBannerBlockCF06Parts, SystemStyleObject>>} HeroBannerBlockCF06Styles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {HeroBannerBlockCF06Styles} [props.styles] - Component styles
 * @returns {React.ReactElement}
 */
export const HeroBannerBlockCF06 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {backgroundMedia, backgroundVideoMobile, headingTitle, subtitle} = component;
  const mediaFields = {backgroundMedia, backgroundVideoMobile};
  const hasBackgroundMedia = hasHeroBannerBackgroundMedia(mediaFields);
  const hasVideo = hasHeroBannerVideo(mediaFields);
  const hasContentSection = Boolean(headingTitle || subtitle);
  const isMobileNaturalVideo = hasVideo && !hasContentSection;

  const wrapperClassName = [
    'hero-banner',
    !hasContentSection && 'hero-banner--cf06-no-content',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box className={wrapperClassName} {...styles.wrapper}>
      <Box className="hero-image-wrapper" {...styles.mediaWrapper}>
        {hasBackgroundMedia ? (
          <HeroBannerBackgroundMedia
            backgroundMedia={backgroundMedia}
            backgroundVideoMobile={backgroundVideoMobile}
            mobileVideoOptions={isMobileNaturalVideo ? HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS : undefined}
            {...styles.backgroundMedia}
          />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
      </Box>

      {hasContentSection ? (
        <Flex className="hero-content-wrapper" {...styles.contentSection}>
          {!isComplete && isFetching ? (
            <Skeleton height="120px" width="100%" />
          ) : (
            <>
              <Box {...styles.titleColumn}>
                {headingTitle && <Heading {...styles.title}>{headingTitle}</Heading>}
              </Box>

              <Box {...styles.subtitleColumn}>
                {subtitle && <Text {...styles.subtitle}>{subtitle}</Text>}
              </Box>
            </>
          )}
        </Flex>
      ) : null}
    </Box>
  );
};

HeroBannerBlockCF06.displayName = 'HeroBannerBlockCF06';
HeroBannerBlockCF06.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
